// The two-minute check: find the one job worth fixing, size it in hours, name
// where it leaks, and say whether the Audit is the right next step.
// Pure data and functions, so the arithmetic can be tested without a browser.

export const AUDIT = {
  price: "$1,500",
  time: "Two weeks",
  // Stripe payment link, live. A client reaches it from their own page, after signing.
  pay: "https://buy.stripe.com/4gMfZh7w98QybBV4Iuc7u00",
};
// For a job too small for the Audit: one small tool, built for that one job.
export const SMALL = { price: "$750", time: "Within a week" };
// The second door, for people who would rather talk before they book anything.
export const CALL = {
  url: "https://cal.com/nomus/fit-call",
  time: "20 minutes",
};

// Five questions describe the job. They are not scored; they are measured.
export const JOB = {
  q: "Which job does your team repeat most?",
  options: [
    {
      id: "onboarding",
      label: "Onboarding a new client",
      noun: "Client onboarding",
    },
    {
      id: "reporting",
      label: "Monthly or weekly reporting",
      noun: "Reporting",
    },
    {
      id: "collecting",
      label: "Collecting documents or information",
      noun: "Collecting documents",
    },
    {
      id: "proposals",
      label: "Preparing proposals or quotes",
      noun: "Preparing proposals",
    },
    {
      id: "requests",
      label: "Handling incoming requests",
      noun: "Handling requests",
    },
    { id: "close", label: "Month-end close", noun: "Month-end close" },
    { id: "other", label: "Something else", noun: "That job" },
  ],
};
export const FREQUENCY = {
  q: "How often does it run?",
  options: [
    {
      id: "daily",
      label: "Every working day",
      runs: 21,
      phrase: "every working day",
    },
    {
      id: "several",
      label: "A few times a week",
      runs: 12,
      phrase: "about 12 times a month",
    },
    {
      id: "weekly",
      label: "About once a week",
      runs: 4,
      phrase: "about 4 times a month",
    },
    {
      id: "monthly",
      label: "About once a month",
      runs: 1,
      phrase: "once a month",
    },
  ],
};
export const DURATION = {
  q: "How much hands-on time does one run take?",
  options: [
    {
      id: "minutes",
      label: "Under half an hour",
      hours: [0.25, 0.5],
      phrase: "under half an hour",
    },
    {
      id: "hour",
      label: "About an hour",
      hours: [0.75, 1.5],
      phrase: "about an hour",
    },
    {
      id: "half",
      label: "About half a day",
      hours: [3, 5],
      phrase: "about half a day",
    },
    {
      id: "day",
      label: "A day or more",
      hours: [7, 10],
      phrase: "a day or more",
    },
  ],
};
export const PEOPLE = {
  q: "How many people touch it before it's done?",
  options: [
    { id: "one", label: "One", count: 1 },
    { id: "few", label: "Two or three", count: 3 },
    { id: "several", label: "Four to six", count: 5 },
    { id: "many", label: "More than six", count: 7 },
  ],
};
export const STALL = {
  q: "Where does it usually stall?",
  options: [
    {
      id: "outside",
      label: "Waiting for information from outside",
      leak: "Waiting for information from outside",
      look: "Every request that leaves the firm: who sends it, where the answer lands, and who notices when it doesn't come.",
    },
    {
      id: "finding",
      label: "Finding things across systems",
      leak: "Finding things across systems",
      look: "Every place the job reads from, and which of them your software already lets AI open.",
    },
    {
      id: "copying",
      label: "Copying between systems",
      leak: "Copying between systems",
      look: "Every time the same detail is typed twice, and which system should own it.",
    },
    {
      id: "approval",
      label: "Waiting for someone to approve",
      leak: "Waiting for approval",
      look: "Who approves what, what they need in front of them to decide, and what could wait in one queue.",
    },
    {
      id: "rework",
      label: "Fixing mistakes afterwards",
      leak: "Rework",
      look: "Where the mistakes enter, and which check would have caught each one.",
    },
  ],
};

// Five questions are scored 0-3: can a tool safely take this job on today?
const four = (a, b, c, d) => [
  { points: 3, label: a },
  { points: 2, label: b },
  { points: 1, label: c },
  { points: 0, label: d },
];
export const READINESS = [
  {
    area: "Where it lives",
    q: "Where does this job live?",
    options: four(
      "In one system that everyone uses",
      "In one system, plus a spreadsheet",
      "Across several tools and inboxes",
      "In people's heads and personal files",
    ),
    look: "Where the job lives today, and the one place it should live.",
  },
  {
    area: "Written down",
    q: "Is the job written down?",
    options: four(
      "A checklist, kept up to date, with owners",
      "A checklist exists, somewhere",
      "Each person does it their own way",
      "Nobody could describe it start to finish",
    ),
    look: "The job, written down step by step, with an owner for each step.",
  },
  {
    area: "AI in use",
    q: "How does the team use AI today?",
    options: four(
      "Paid seats for everyone, with a written policy",
      "Some paid seats, no shared rules",
      "People use their own accounts",
      "Not at all, or it's banned",
    ),
    look: "Which AI the team uses, on which accounts, and under what rules.",
  },
  {
    area: "AI access",
    q: "Can your AI see the systems this job touches?",
    options: four(
      "Yes, at least one, and it can read it",
      "We export files or paste data in by hand",
      "It only writes emails and documents",
      "No",
    ),
    look: "What your software already lets AI read, before anything is built.",
  },
  {
    area: "Access and security",
    q: "How are logins and access handled?",
    options: four(
      "Two-step sign-in everywhere, access by role",
      "Two-step sign-in on most systems",
      "Some shared logins",
      "Shared passwords and no rules",
    ),
    look: "Logins and access: who can reach what, and what a tool may reach.",
  },
];

// One list of steps for the page: five to measure, five to score.
export const STEPS = [
  { kind: "job", ...JOB },
  { kind: "frequency", ...FREQUENCY },
  { kind: "duration", ...DURATION },
  { kind: "people", ...PEOPLE },
  { kind: "stall", ...STALL },
  ...READINESS.map((r, i) => ({ kind: "ready", index: i, ...r })),
];
export const MAX_READINESS = READINESS.length * 3;
const SECURITY = READINESS.length - 1;

// Hours a month, as a range, from the two answers that measure it.
export function hours(frequencyIndex, durationIndex) {
  const { runs } = FREQUENCY.options[frequencyIndex];
  const [low, high] = DURATION.options[durationIndex].hours;
  return [Math.round(runs * low), Math.round(runs * high)];
}

// "small" is not worth a tool. "worth" earns an audit. "large" is costing a lot already.
export function size([, high]) {
  return high < 10 ? "small" : high < 40 ? "worth" : "large";
}

export const readiness = (points) => points.reduce((s, v) => s + v, 0);

// Shared passwords and no rules are a gate: nothing gets built until that is fixed.
export function readyBand(points) {
  if (points[SECURITY] === 0) return "foundations";
  const t = readiness(points);
  return t <= 5 ? "foundations" : t <= 10 ? "ready" : "strong";
}

const VERDICTS = {
  small: {
    title: "This job is too small for a full tool.",
    text: "A two-week Audit and a full tool would cost more than the hours they give back. A small tool would not: one tool for this one job, built within a week, for a fixed price.",
    audit: false,
    small: true,
  },
  foundations: {
    title: "Worth fixing. The foundations come first.",
    text: "The hours are real, and a tool can't safely take the job on yet. The Audit starts with where the job lives and who can reach what, then prices the tool.",
    audit: true,
  },
  ready: {
    title: "This job is worth an audit.",
    text: "The hours are real and the foundations hold. The Audit maps the job, names what the tool does, and fixes its price.",
    audit: true,
  },
  strong: {
    title: "This job should already be running on a tool.",
    text: "The hours are real, the job is written down and your systems are ready. The Audit is short work here: it fixes the scope and the price.",
    audit: true,
  },
};

// Everything the result page needs, from the ten answers (each an option index).
export function result(answers) {
  const [job, frequency, duration, people, stall, ...ready] = answers;
  const points = ready.map((a, i) => READINESS[i].options[a].points);
  const range = hours(frequency, duration);
  const scale = size(range);
  const band = readyBand(points);
  const verdict = VERDICTS[scale === "small" ? "small" : band];
  const j = JOB.options[job];
  const f = FREQUENCY.options[frequency];
  const d = DURATION.options[duration];
  const p = PEOPLE.options[people];
  const s = STALL.options[stall];

  // What the Audit will look at: the leak, the weakest foundations, and the handoffs.
  const look = [s.look];
  points
    .map((pts, i) => ({ pts, i }))
    .filter((x) => x.pts <= 1)
    .sort((a, b) => a.pts - b.pts || b.i - a.i)
    .slice(0, 2)
    .forEach((x) => look.push(READINESS[x.i].look));
  if (p.count >= 5 && look.length < 3)
    look.push(
      "Every handoff between the people who touch it, and which ones a tool removes.",
    );
  if (look.length < 2)
    look.push(
      "The job from first email to sign-off, and what the tool does at each step.",
    );

  return {
    job: j.noun,
    sentence: `${j.noun} runs ${f.phrase} and takes ${d.phrase} each time.`,
    hours: range,
    scale,
    leak: s.leak,
    people: p.count,
    points,
    readiness: readiness(points),
    band,
    gated: points[SECURITY] === 0,
    verdict,
    look: look.slice(0, 3),
  };
}

// Answers travel in the URL as ten digits, one option index per question.
export const encode = (answers) => answers.join("");
export function decode(value) {
  if (typeof value !== "string" || value.length !== STEPS.length) return null;
  const answers = [...value].map(Number);
  const ok = answers.every(
    (a, i) => Number.isInteger(a) && a >= 0 && a < STEPS[i].options.length,
  );
  return ok ? answers : null;
}

// One line for the contact form, so the first email already names the job.
export function summary(answers) {
  const r = result(answers);
  return `Our check: ${r.sentence} About ${r.hours[0]}–${r.hours[1]} hours a month. It stalls at: ${r.leak.toLowerCase()}. Foundations ${r.readiness}/${MAX_READINESS}.`;
}

// The booking page opens with the check result in its notes, so the call starts from the job.
export const callLink = (answers) =>
  answers
    ? `${CALL.url}?notes=${encodeURIComponent(summary(answers))}`
    : CALL.url;
